import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Stethoscope, Plus, Trash, PencilSimple } from "@phosphor-icons/react";

const DAYS = [["Sen", 0], ["Sel", 1], ["Rab", 2], ["Kam", 3], ["Jum", 4], ["Sab", 5], ["Min", 6]];
const EMPTY = { petugas_nama: "", recurrence: "weekly", days: [], dates: "", start_time: "", end_time: "", max_patients: 8, valid_from: "", valid_until: "", active: true };

export default function PracticeSessions() {
  const [staff, setStaff] = useState([]);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/practice-sessions").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => {
    api.get("/staff").then((r) => setStaff(r.data)).catch(() => setStaff([]));
    load();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleDay = (d) => setForm((f) => ({ ...f, days: f.days.includes(d) ? f.days.filter((x) => x !== d) : [...f.days, d] }));

  const reset = () => { setForm(EMPTY); setEditId(null); };

  const submit = async () => {
    const payload = {
      petugas_nama: form.petugas_nama, recurrence: form.recurrence,
      days: form.recurrence === "weekly" ? form.days : [],
      dates: form.recurrence === "date" ? form.dates.split(",").map((s) => s.trim()).filter(Boolean) : [],
      start_time: form.start_time, end_time: form.end_time,
      max_patients: Number(form.max_patients), valid_from: form.valid_from || null,
      valid_until: form.valid_until || null, active: form.active,
    };
    setBusy(true);
    try {
      if (editId) await api.put(`/practice-sessions/${editId}`, payload);
      else await api.post("/practice-sessions", payload);
      toast.success(editId ? "Sesi praktik diperbarui" : "Sesi praktik dibuat");
      reset(); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const edit = (r) => {
    setEditId(r.id);
    setForm({ petugas_nama: r.petugas_nama, recurrence: r.recurrence === "dates" ? "date" : (r.recurrence || "weekly"), days: r.days || [], dates: (r.dates || []).join(", "), start_time: r.start_time || "", end_time: r.end_time || "", max_patients: r.max_patients || 8, valid_from: r.valid_from || "", valid_until: r.valid_until || "", active: r.active !== false });
  };
  const del = async (r) => {
    if (!window.confirm(`Hapus sesi praktik ${r.petugas_nama}?`)) return;
    try { await api.delete(`/practice-sessions/${r.id}`); toast.success("Dihapus"); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const inp = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm";
  const scope = (r) => (r.recurrence === "date" || r.recurrence === "dates") ? (r.dates || []).join(", ") : (r.days || []).map((d) => DAYS.find((x) => x[1] === d)?.[0]).join(", ");

  return (
    <div className="animate-fade-up space-y-5" data-testid="practice-page">
      <div className="flex items-center gap-2"><Stethoscope size={26} weight="duotone" className="text-[#7C4DE0]" /><h1 className="font-heading text-3xl font-extrabold text-slate-800">Sesi Praktik / Antrean</h1></div>
      <p className="text-sm text-slate-500">Jadwal praktik berbasis kuota &amp; durasi (Gerai). Pasien memilih layanan, petugas &amp; tanggal; jam &amp; nomor antrean diatur otomatis.</p>

      <div className="rounded-2xl border border-rose-100/70 bg-white p-5 shadow-sm">
        <h2 className="mb-3 font-heading text-base font-bold text-slate-700">{editId ? "Edit Sesi" : "Tambah Sesi Praktik"}</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <label className="text-xs font-semibold text-slate-500">Petugas
            <select data-testid="pr-petugas" value={form.petugas_nama} onChange={(e) => set("petugas_nama", e.target.value)} className={inp}>
              <option value="">Pilih petugas</option>
              {staff.map((s) => <option key={s.id} value={s.nama}>{s.nama}{s.jenis ? ` (${s.jenis})` : ""}</option>)}
            </select>
          </label>
          <label className="text-xs font-semibold text-slate-500">Pengulangan
            <select data-testid="pr-recurrence" value={form.recurrence} onChange={(e) => set("recurrence", e.target.value)} className={inp}>
              <option value="weekly">Mingguan (hari)</option>
              <option value="date">Tanggal tertentu</option>
            </select>
          </label>
          <label className="text-xs font-semibold text-slate-500">Maks. Pasien
            <input data-testid="pr-max" type="number" min="1" value={form.max_patients} onChange={(e) => set("max_patients", e.target.value)} className={inp} />
          </label>
          {form.recurrence === "weekly" ? (
            <div className="text-xs font-semibold text-slate-500 sm:col-span-2 lg:col-span-3">Hari
              <div className="mt-1 flex flex-wrap gap-1.5">
                {DAYS.map(([lbl, d]) => (
                  <button type="button" key={d} data-testid={`pr-day-${d}`} onClick={() => toggleDay(d)} className={`rounded-lg px-3 py-1.5 text-xs font-bold ${form.days.includes(d) ? "bg-[#7C4DE0] text-white" : "bg-slate-100 text-slate-600"}`}>{lbl}</button>
                ))}
              </div>
            </div>
          ) : (
            <label className="text-xs font-semibold text-slate-500 sm:col-span-2 lg:col-span-3">Tanggal (pisahkan koma, YYYY-MM-DD)
              <input data-testid="pr-dates" value={form.dates} onChange={(e) => set("dates", e.target.value)} placeholder="2026-09-01, 2026-09-08" className={inp} />
            </label>
          )}
          <label className="text-xs font-semibold text-slate-500">Jam Mulai
            <input data-testid="pr-start" type="time" value={form.start_time} onChange={(e) => set("start_time", e.target.value)} className={inp} />
          </label>
          <label className="text-xs font-semibold text-slate-500">Jam Selesai
            <input data-testid="pr-end" type="time" value={form.end_time} onChange={(e) => set("end_time", e.target.value)} className={inp} />
          </label>
          <label className="text-xs font-semibold text-slate-500">Berlaku Dari (opsional)
            <input data-testid="pr-from" type="date" value={form.valid_from} onChange={(e) => set("valid_from", e.target.value)} className={inp} />
          </label>
          <label className="text-xs font-semibold text-slate-500">Berlaku Sampai (opsional)
            <input data-testid="pr-until" type="date" value={form.valid_until} onChange={(e) => set("valid_until", e.target.value)} className={inp} />
          </label>
        </div>
        <div className="mt-4 flex gap-2">
          <button data-testid="pr-submit" disabled={busy} onClick={submit} className="inline-flex items-center gap-1.5 rounded-xl bg-[#7C4DE0] px-4 py-2 text-sm font-bold text-white hover:bg-[#6A38C7] disabled:opacity-60"><Plus size={16} weight="bold" /> {busy ? "..." : editId ? "Simpan" : "Tambah"}</button>
          {editId && <button data-testid="pr-cancel" onClick={reset} className="rounded-xl bg-slate-100 px-4 py-2 text-sm font-bold text-slate-600">Batal</button>}
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-rose-100/70 bg-white shadow-sm">
        <table className="w-full text-sm" data-testid="pr-table">
          <thead className="bg-[#F7F0FF] text-left text-xs uppercase text-slate-500">
            <tr><th className="px-4 py-3">Petugas</th><th className="px-4 py-3">Jadwal</th><th className="px-4 py-3">Jam</th><th className="px-4 py-3">Maks</th><th className="px-4 py-3 text-right">Aksi</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">Belum ada sesi praktik.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`pr-row-${r.id}`} className="border-t border-rose-50">
                <td className="px-4 py-3 font-semibold text-slate-800">{r.petugas_nama}{r.active === false && <span className="ml-1 text-xs text-slate-400">(nonaktif)</span>}</td>
                <td className="px-4 py-3 text-slate-600">{scope(r)}</td>
                <td className="px-4 py-3 text-slate-600">{r.start_time}–{r.end_time}</td>
                <td className="px-4 py-3 text-slate-600">{r.max_patients}</td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`pr-edit-${r.id}`} onClick={() => edit(r)} className="mr-2 inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600"><PencilSimple size={14} /> Edit</button>
                  <button data-testid={`pr-del-${r.id}`} onClick={() => del(r)} className="inline-flex items-center gap-1 rounded-lg bg-rose-50 px-2.5 py-1 text-xs font-bold text-rose-600"><Trash size={14} /> Hapus</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
